import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff, deduplicateRequest } from '../common';
import { NAMESPACES } from '../configuration';

const BASE_URL = NAMESPACES.management_api.providers;

// Helper function to configure the Axios instance with auth interceptor
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Wrap with cache for GET requests
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'management_api',
  group: 'providers',
  defaultTtl: '2m',
  hashLargeSegments: true,
  emitMeta: true,
});

/**
 * DELETE operations - use auth interceptor, clear cache after mutations
 */

// Delete multiple providers by IDs
export const deleteProvidersSelectedByIds = async (providerIds: number[]) => {
  const res = await retryWithBackoff(() =>
    api.delete('/DeleteProvidersSelectedByIds', {
      data: { provider_ids: providerIds }
    })
  );
  await cachedApi.clear();
  return res;
};

// Delete multiple provider categories by IDs
export const deleteProvidersCategoriesSelectedByIds = async (categoryIds: number[]) => {
  const res = await retryWithBackoff(() =>
    api.delete('/DeleteProviderCategoriesSelectedByIds', {
      data: { category_ids: categoryIds }
    })
  );
  await cachedApi.clear();
  return res;
};

// Delete single provider by ID
export const deleteProviderById = async (providerId: number) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/DeleteProviderById/${providerId}`)
  );
  await cachedApi.clear();
  return res;
};

// Delete single provider category by ID
export const deleteProviderCategoryById = async (providerCategoryId: number) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/DeleteProviderCategoryById/${providerCategoryId}`)
  );
  await cachedApi.clear();
  return res;
};

/**
 * GET operations - use cached API with retry and deduplication
 */

// Get all providers by company ID - frequently called, use deduplication
export const getAllProvidersByCompanyId = async (localId: string) => {
  return await deduplicateRequest(
    `getAllProviders:${localId}`,
    () => retryWithBackoff(() => cachedApi.get(`/GetAllProvidersByCompanyId/${localId}`))
  );
};

// Get single provider by ID
export const getProviderByProviderId = async (providerId: string) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetProviderByProviderId/${providerId}`)
  );
};

// Get all provider categories - frequently called, use deduplication
export const getAllProviderCategoriesByCompanyId = async (localId: string) => {
  return await deduplicateRequest(
    `getAllProviderCategories:${localId}`,
    () => retryWithBackoff(() => cachedApi.get(`/GetAllProviderCategoriesByCompanyId/${localId}`))
  );
};

// Get all providers by local ID - frequently called, use deduplication
export const getAllProvidersByLocalId = async (localId: number) => {
  return await deduplicateRequest(
    `getAllProvidersByLocal:${localId}`,
    () => retryWithBackoff(() => cachedApi.get(`/GetAllProvidersByLocalId/${localId}`))
  );
};

/**
 * PUT operations - use auth interceptor, retry, and clear cache after mutations
 */

// Update provider by ID
export const updateProviderById = async (providerId: number, providerData: any) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateProviderById/${providerId}`, providerData)
  );
  await cachedApi.clear();
  return res;
};

// Update provider category by ID
export const updateProviderCategoryById = async (providerCategoryId: number, providerCategoryData: any) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateProviderCategoryById/${providerCategoryId}`, providerCategoryData)
  );
  await cachedApi.clear();
  return res;
};

/**
 * POST operations - use auth interceptor, retry, and clear cache after mutations
 */

// Create new provider
export const createProvider = async (providerData: any) => {
  const res = await retryWithBackoff(() =>
    api.post('/CreateProvider', providerData)
  );
  await cachedApi.clear();
  return res;
};

// Create new provider category
export const createProviderCategory = async (providerCategoryData: any) => {
  const res = await retryWithBackoff(() =>
    api.post('/CreateProviderCategory', providerCategoryData)
  );
  await cachedApi.clear();
  return res;
};


/**
 * Category Assignment Operations
 * Backend API documentation preserved for reference
 */

/*
assign_categories_parser = reqparse.RequestParser()
assign_categories_parser.add_argument(
    'category_ids',
    type=int,
    action='append',
    required=True,
    location='json',
    help='List of category IDs to assign to the provider'
)

@ns.route('/AssignCategoriesToProvider/<int:provider_id>')
@ns.param('provider_id', 'Provider ID')
class AssignCategoriesToProvider(Resource):
    @ns.doc(security='bearerAuth')
    @ns.expect(assign_categories_parser)
    @ns.marshal_with(provider_model)
    @jwt_required()
    def post(self, provider_id):
        """Assign one or more categories to a provider (adds to existing categories)"""
        p = Provider.query.get_or_404(provider_id)
        args = request.get_json() or {}
        category_ids = args.get('category_ids', [])
        
        if not category_ids:
            ns.abort(400, "No category IDs provided")
        
        # Validate that all categories exist and belong to the same company as the provider
        categories = ProviderCategory.query.filter(
            ProviderCategory.category_id.in_(category_ids)
        ).all()
        
        if len(categories) != len(category_ids):
            ns.abort(400, "One or more category IDs are invalid")
        
        # Verify all categories belong to the same company as the provider
        for cat in categories:
            if cat.company_id != p.company_id:
                ns.abort(400, f"Category '{cat.name}' (ID: {cat.category_id}) does not belong to the same company as the provider")
        
        # Get current category IDs
        current_category_ids = {cat.category_id for cat in p.categories}
        new_category_ids = set(category_ids)
        
        # Add only new categories (avoid duplicates)
        categories_to_add = [cat for cat in categories if cat.category_id not in current_category_ids]
        
        if categories_to_add:
            p.categories.extend(categories_to_add)
            db.session.commit()
            return p, 200
        else:
            return {'message': 'All specified categories are already assigned to this provider'}, 200

unassign_categories_parser = reqparse.RequestParser()
unassign_categories_parser.add_argument(
    'category_ids',
    type=int,
    action='append',
    required=True,
    location='json',
    help='List of category IDs to unassign from the provider'
)

@ns.route('/UnassignCategoriesFromProvider/<int:provider_id>')
@ns.param('provider_id', 'Provider ID')
class UnassignCategoriesFromProvider(Resource):
    @ns.doc(security='bearerAuth')
    @ns.expect(unassign_categories_parser)
    @ns.marshal_with(provider_model)
    @jwt_required()
    def post(self, provider_id):
        """Remove one or more categories from a provider"""
        p = Provider.query.get_or_404(provider_id)
        args = request.get_json() or {}
        category_ids = args.get('category_ids', [])
        
        if not category_ids:
            ns.abort(400, "No category IDs provided")
        
        # Get current categories
        current_categories = {cat.category_id: cat for cat in p.categories}
        
        # Remove specified categories
        categories_to_remove = [cat for cat_id, cat in current_categories.items() if cat_id in category_ids]
        
        if categories_to_remove:
            for cat in categories_to_remove:
                p.categories.remove(cat)
            db.session.commit()
            return p, 200
        else:
            return {'message': 'None of the specified categories were assigned to this provider'}, 200
*/

// Assign categories to provider
export const assignCategoriesToProvider = async (providerId: number, categoryIds: number[]) => {
  const res = await retryWithBackoff(() =>
    api.post(`/AssignCategoriesToProvider/${providerId}`, { category_ids: categoryIds })
  );
  await cachedApi.clear();
  return res;
};

export const unassignCategoriesFromProvider = async (providerId: number, categoryIds: number[]) => {
  const res = await retryWithBackoff(() =>
    api.post(`/UnassignCategoriesFromProvider/${providerId}`, { category_ids: categoryIds })
  );
  await cachedApi.clear();
  return res;
};

